import { g as getMediaQuery } from './media-queries-5a9283a8.js';
import { a as atBreakpointChange } from './at-breakpoint-change-929474bb.js';
import { i as intersectionWatcher } from './intersection-watcher-50753dc4.js';
import './srraf.es-487187f3.js';

const prefersReducedMotion = () => {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
};

/**
 * Extracts background videos from 'template's to be added to the DOM, and controls pause/play state through
 * a11y triggers and an intersection observer.
 * If two videos are enabled for desktop and mobile, update video templates by screen size.
 */

class BackgroundVideo extends HTMLElement {
  connectedCallback() {
    const videoTemplate = this.querySelector("[js-background-video-template]");

    const hasVideo = this.dataset.hasVideo === "true";
    const hasMobileVideo = this.dataset.hasMobileVideo === "true";

    if (hasVideo && !hasMobileVideo) {
      this.#setVideoTemplate(videoTemplate, this);
    } else if (hasVideo & hasMobileVideo) {
      const videoMobileTemplate = this.querySelector(
        "[js-mobile-background-video-template]",
      );
      this.#initResponsiveVideoTemplate(
        this,
        videoTemplate,
        videoMobileTemplate,
      );
    }
  }

  disconnectedCallback() {
    this.removeEventListener("toggle", this.handleToggle, true);
  }

  #initResponsiveVideoTemplate(container, videoTemplate, videoMobileTemplate) {
    const setTemplateByWidth = () => {
      if (window.matchMedia(getMediaQuery("tablet")).matches) {
        this.#setVideoTemplate(videoTemplate, container);
      } else {
        this.#setVideoTemplate(videoMobileTemplate, container);
      }
    };

    setTemplateByWidth();
    atBreakpointChange(720, setTemplateByWidth);
  }

  #setVideoTemplate(videoTemplate, container) {
    const pauseBtn = container.querySelector("[js-video-pause-button]");
    const muteBtn = container.querySelector("[js-video-mute-button]");
    const progressRange = container.querySelector("[js-video-progress-range]");
    const currentVideoContainer = container.querySelector(
      "[js-current-background-video-container]",
    );

    const currentVideoTemplate = videoTemplate.content.cloneNode(true);
    const currentVideoEl = currentVideoTemplate.querySelector(
      "[data-background-video]",
    );

    if (currentVideoEl) {
      currentVideoEl.muted = true;

      // Video elements cloned out of an inert <template> and inserted via JS don't
      // reliably pick up the browser's native loading="lazy" behavior the way a
      // parser-inserted element would - some engines just never start fetching the
      // source, leaving the element stuck at readyState 0 forever (seen concretely:
      // Safari/iOS, and inconsistently elsewhere depending on scroll timing). We
      // already gate actual playback ourselves via the intersection observer below,
      // so native lazy-loading is redundant here - strip it and force resource
      // selection to start explicitly instead of hoping the browser notices.
      currentVideoEl.removeAttribute("loading");
    }

    currentVideoContainer.innerHTML = "";
    currentVideoContainer.appendChild(currentVideoTemplate);

    if (currentVideoEl) {
      currentVideoEl.load();
    }

    // Callback for intersectionWatcher helps leverage context from this file without needing to pass it
    const videoWatcherCallback = (visible) =>
      this.#visibleVideoWatcher(currentVideoEl, visible);

    this.#setPlayPause(currentVideoEl, "pause");
    this.#setupPauseButton(currentVideoEl, pauseBtn);
    this.#setupMuteButton(currentVideoEl, muteBtn);
    this.#setupProgressBar(currentVideoEl, progressRange);
    this.#setupControlsReveal(container, currentVideoEl);
    intersectionWatcher(currentVideoEl, { callback: videoWatcherCallback });
  }

  #setupPauseButton(video, pauseBtn) {
    if (!pauseBtn || !video) return

    const {
      strings: { accessibility: strings },
    } = window.theme;

    const updatePauseButtonState = () => {
      const isPlaying = !video.paused;
      pauseBtn.dataset.playing = isPlaying;
      pauseBtn.setAttribute(
        "aria-label",
        isPlaying ? strings.pauseVideo : strings.playVideo,
      );
    };

    // Reflect the video's current state on load
    updatePauseButtonState();

    // Keep the button in sync no matter what triggers play/pause (click, a11y tabbing,
    // or the intersection observer auto-pausing/resuming as the video scrolls in and out of view)
    video.addEventListener("play", updatePauseButtonState);
    video.addEventListener("pause", updatePauseButtonState);

    pauseBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Pausing manually is respected as priority state by the intersection observer
      if (video.paused) {
        this.#setPlayPause(video, "play");
        video.dataset.a11yPriorityPause = false;
      } else {
        this.#setPlayPause(video, "pause");
        video.dataset.a11yPriorityPause = true;
      }
    });
  }

  #setupMuteButton(video, muteBtn) {
    if (!muteBtn || !video) return

    const {
      strings: { accessibility: strings },
    } = window.theme;

    const updateMuteButtonState = () => {
      muteBtn.dataset.muted = video.muted;
      muteBtn.setAttribute(
        "aria-label",
        video.muted ? strings.unmuteVideo : strings.muteVideo,
      );
    };

    // Reflect the video's current (initially muted, for autoplay) state on load
    updateMuteButtonState();

    muteBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      video.muted = !video.muted;
      updateMuteButtonState();
    });
  }

  // Reveals the pause/mute/scrub controls only when relevant, instead of always showing them:
  //   - Desktop (mouse): hovering the video reveals the controls; clicking the video body
  //     while they're already showing toggles play/pause (a click directly on the pause
  //     button always toggles, handled in #setupPauseButton).
  //   - Touch devices don't fire hover, so tapping the plain video body follows a 2-tap
  //     pattern instead: the first tap only reveals the controls, a second tap toggles
  //     play/pause. Tapping anywhere outside the video hides the controls again.
  // Skipped for the product-media lightbox variant so it doesn't swallow taps meant to
  // open the lightbox; that variant's controls still reveal on hover/focus via CSS.
  #setupControlsReveal(container, video) {
    if (!container || !video) return
    if (container.querySelector("[js-media-lightbox-link]")) return

    container.dataset.controlsVisible = "false";

    const showControls = () => {
      container.dataset.controlsVisible = "true";
    };

    const hideControls = () => {
      container.dataset.controlsVisible = "false";
    };

    container.addEventListener("mouseenter", showControls);
    container.addEventListener("mouseleave", hideControls);

    container.addEventListener("click", (e) => {
      // Clicks on the pause button, mute button, or scrub bar stop propagation
      // themselves before reaching here, so this only fires for taps on the plain
      // video body.
      if (container.dataset.controlsVisible === "true") {
        if (video.paused) {
          this.#setPlayPause(video, "play");
          video.dataset.a11yPriorityPause = false;
        } else {
          this.#setPlayPause(video, "pause");
          video.dataset.a11yPriorityPause = true;
        }
      } else {
        showControls();
      }
    });

    document.addEventListener("click", (e) => {
      if (!container.contains(e.target)) {
        hideControls();
      }
    });
  }

  #setupProgressBar(video, progressRange) {
    if (!progressRange || !video) return

    let isScrubbing = false;

    const updateFill = (percent) => {
      progressRange.style.setProperty("--video-progress-percent", `${percent}%`);
    };

    const updateFromVideo = () => {
      if (isScrubbing || !video.duration || !isFinite(video.duration)) return

      const percent = (video.currentTime / video.duration) * 100;
      progressRange.value = percent;
      updateFill(percent);
    };

    video.addEventListener("timeupdate", updateFromVideo);
    video.addEventListener("loadedmetadata", updateFromVideo);

    progressRange.addEventListener("pointerdown", (e) => {
      e.stopPropagation();
      isScrubbing = true;
    });

    progressRange.addEventListener("input", () => {
      const percent = Number(progressRange.value);
      updateFill(percent);

      if (video.duration && isFinite(video.duration)) {
        video.currentTime = (percent / 100) * video.duration;
      }
    });

    ["pointerup", "pointercancel", "change"].forEach((eventName) => {
      progressRange.addEventListener(eventName, () => {
        isScrubbing = false;
      });
    });

    progressRange.addEventListener("click", (e) => {
      // Prevent scrub-bar interactions from bubbling to a parent lightbox trigger
      e.stopPropagation();
    });
  }

  #visibleVideoWatcher(video, visible) {
    // Prioritize browser and device settings
    if (prefersReducedMotion()) {
      return
    }

    if (visible && video.paused) {
      // Fade in video from poster for smooth transition, only on first entry
      if (video.dataset.videoLoaded === "false") {
        video.classList.add("background-video__video--fade-in");
        video.dataset.videoLoaded = "true";
      }

      // Play via intersection entry should only apply when paused state not from a11y tabbing
      if (video.dataset.a11yPriorityPause === "false") {
        this.#setPlayPause(video, "play");
      }
    }

    if (!visible && !video.paused) {
      this.#setPlayPause(video, "pause");
    }
  }

  #setPlayPause(video, state) {
    if (state === "pause") {
      video.pause();
    } else {
      video.play();
    }
  }
}

if (!customElements.get("background-video")) {
  customElements.define("background-video", BackgroundVideo);
}
